import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { CONSOLE_LINK, PRIMARY_NAV } from '@/content/nav';
import { BRAND, SITE } from '@/content/site';
import { PRODUCT_CATEGORIES } from '@/content/products';
import { Icon } from './Icon';
import { MegaMenu } from './MegaMenu';
import { SearchOverlay } from './SearchOverlay';
import { ThemeToggle } from './Controls';
import { Wordmark } from '@/illustrations/Mark';

const SEARCH_TRIGGER_HINT = '⌘K';

/**
 * 顶部导航。
 *
 * 桌面端：品牌字标 + 主导航（含「产品」大菜单）+ 搜索 + 工具区。
 * 移动端：抽屉，产品类目收进手风琴。
 * 半透明毛玻璃底，滚动后才出现描边，回到顶部时保持干净。
 */
export function Header() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const navItemClass = ({ isActive }: { isActive: boolean }) =>
    [
      'whitespace-nowrap rounded-btn px-3 py-2 text-sm transition-colors',
      isActive ? 'text-brand' : 'text-fg hover:text-brand',
    ].join(' ');

  const drawerItemClass = ({ isActive }: { isActive: boolean }) =>
    [
      'rounded-lg px-3 py-3 text-sm transition-colors',
      isActive ? 'bg-surface-2 text-fg' : 'text-fg-muted hover:bg-surface-2 hover:text-fg',
    ].join(' ');

  return (
    <header
      className={[
        'sticky top-0 z-40 border-b backdrop-blur-md',
        scrolled ? 'border-line' : 'border-transparent',
      ].join(' ')}
      style={{ backgroundColor: 'var(--header-bg)' }}
    >
      <div className="container-page flex h-16 items-center gap-2">
        <Link to="/" aria-label="回到首页" className="shrink-0">
          <Wordmark />
        </Link>

        <nav aria-label="主导航" className="ml-2 hidden shrink-0 items-center gap-0.5 lg:flex">
          {PRIMARY_NAV.map((item) =>
            item.to === '/products' ? (
              <MegaMenu key={item.to} />
            ) : (
              <NavLink key={item.to} to={item.to} className={navItemClass}>
                {item.label}
              </NavLink>
            ),
          )}
        </nav>

        <div className="flex-1" />

        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          className="hidden w-52 items-center gap-2 rounded-btn border border-line bg-surface-2/60 px-3 py-2 text-sm text-fg-muted transition-colors hover:border-line-strong hover:text-fg xl:inline-flex"
        >
          <Icon name="search" size={16} />
          <span className="hidden flex-1 text-left xl:inline">搜索</span>
          <kbd className="rounded border border-line bg-surface-1 px-1.5 py-0.5 font-mono text-[0.625rem]">
            {SEARCH_TRIGGER_HINT}
          </kbd>
        </button>

        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          aria-label="站内搜索"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-btn border border-line text-fg-muted transition-colors hover:text-fg xl:hidden"
        >
          <Icon name="search" size={17} />
        </button>

        <ThemeToggle className="rounded-btn" />

        <a
          href={CONSOLE_LINK.to}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden shrink-0 items-center gap-1.5 whitespace-nowrap rounded-btn px-3 py-2.5 text-sm text-fg transition-colors hover:text-brand xl:inline-flex"
        >
          控制台
          <Icon name="external" size={14} />
        </a>

        <a
          href={SITE.platformUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden shrink-0 items-center gap-1.5 whitespace-nowrap rounded-btn bg-grad-cta px-5 py-2.5 text-sm font-medium text-grad-fg transition-[filter] hover:brightness-110 sm:inline-flex"
        >
          进入平台
          <Icon name="external" size={14} />
        </a>

        <Link
          to="/contact"
          className="hidden shrink-0 whitespace-nowrap rounded-btn border border-line-strong px-5 py-2.5 text-sm text-fg transition-colors hover:border-brand hover:text-brand lg:inline-flex"
        >
          联系我们
        </Link>

        <button
          type="button"
          onClick={() => setDrawerOpen((value) => !value)}
          aria-expanded={drawerOpen}
          aria-controls="mobile-nav"
          aria-label={drawerOpen ? '关闭菜单' : '打开菜单'}
          className="inline-flex h-9 w-9 items-center justify-center rounded-btn border border-line text-fg lg:hidden"
        >
          <Icon name={drawerOpen ? 'close' : 'menu'} size={18} />
        </button>
      </div>

      {drawerOpen ? (
        <div
          id="mobile-nav"
          className="max-h-[80dvh] overflow-y-auto border-t border-line bg-surface-1 lg:hidden"
        >
          <nav aria-label="移动端导航" className="container-page flex flex-col py-3">
            <NavLink to="/" className={drawerItemClass}>
              首页
            </NavLink>

            <details className="group">
              <summary className="flex cursor-pointer list-none items-center justify-between rounded-lg px-3 py-3 text-sm text-fg-muted marker:hidden hover:bg-surface-2 hover:text-fg">
                产品
                <Icon name="chevronDown" size={15} className="transition-transform group-open:rotate-180" />
              </summary>
              <div className="flex flex-col pb-1 pl-3">
                {PRODUCT_CATEGORIES.map((category) => (
                  <Link
                    key={category.id}
                    to={`/products#cat-${category.id}`}
                    className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm text-fg-muted hover:bg-surface-2 hover:text-fg"
                  >
                    <Icon name={category.icon} size={15} className="text-fg-subtle" />
                    {category.name}
                  </Link>
                ))}
                <Link to="/products" className="px-3 py-2.5 text-sm font-medium text-brand">
                  查看全部产品
                </Link>
              </div>
            </details>

            {PRIMARY_NAV.filter((item) => item.to !== '/products').map((item) => (
              <NavLink key={item.to} to={item.to} className={drawerItemClass}>
                {item.label}
              </NavLink>
            ))}

            <NavLink to="/contact" className={drawerItemClass}>
              联系我们
            </NavLink>

            <a
              href={CONSOLE_LINK.to}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between rounded-lg px-3 py-3 text-sm text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg"
            >
              控制台（账号、组织与接入）
              <Icon name="external" size={14} />
            </a>

            <a
              href={SITE.platformUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-btn bg-grad-cta px-3 py-3 text-sm font-medium text-grad-fg"
            >
              进入平台（{BRAND.name} Platform）
              <Icon name="external" size={14} />
            </a>
          </nav>
        </div>
      ) : null}

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
